import React, { useState } from 'react';
import { Upload, FileText, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const GoalLineReportGenerator = () => {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearAll = () => {
    setFile(null);
    setReport(null);
    setLoading(false);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Security validations
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    const allowedTypes = ['text/csv', 'application/vnd.ms-excel'];
    const allowedExtensions = ['.csv'];
    
    if (uploadedFile.size > maxFileSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const fileExtension = uploadedFile.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (!allowedExtensions.includes(fileExtension) || !allowedTypes.includes(uploadedFile.type)) {
      if (!uploadedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Please upload a CSV file only');
        return;
      }
    }

    setFile(uploadedFile);
    setError(null);
  };

  const processData = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('csvFile', file);

      // Send file to server for processing using the new generic endpoint
      const response = await fetch(`${domainConfig.apiUrl}/api/reports/goalline`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const result = await response.json();
      setReport(result.report);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };


  const renderReport = () => {
    if (!report) return null;

    return (
      <Box sx={{ mt: 2, bgcolor: 'white', minHeight: '100vh' }}>
        {/* Report Content */}
        <Box sx={{ p: 1 }}>

        {/* Complete Goal Line Report - All in one section */}
        <Box sx={{ mb: 2 }} data-section="goalline-summary-section">
          {/* Header Section */}
          <ReportHeader 
            title="GOAL LINE ANALYSIS"
            logos={report.logos}
            fileName={file?.name}
          />
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
            GOAL LINE SUMMARY (5 yards or less to goal line)
          </Typography>
          
          <Box component="ul" sx={{ 
            listStyle: 'none', 
            padding: 0, 
            margin: 0,
            '& li': {
              '&:before': {
                content: '""',
                color: 'text.primary',
                fontWeight: 'bold'
              }
            }
          }}>
            <li><Typography variant="h6" component="span">
                <strong>Total Plays:</strong> {report.totalGoalLinePlays} [All plays from 1-5 yards to goal line]
              </Typography>
            </li>
            <li><Typography variant="h6" component="span">
                <strong>Total Runs:</strong> {report.totalRuns} ({report.runPercentage}%) 
                {parseFloat(report.runPercentage) >= 80 && ' ❗ EXTREMELY VALUABLE RUN TENDENCY'}
              </Typography>
            </li>
            <li><Typography variant="h6" component="span">
                <strong>Total Passes:</strong> {report.totalPasses} ({report.passPercentage}%)
                {parseFloat(report.passPercentage) >= 80 && ' ❗ EXTREMELY VALUABLE PASS TENDENCY'}
              </Typography>
            </li>
          </Box>

          {/* Personnel Analysis - Part of same section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2 }}>
              PERSONNEL GROUPING ANALYSIS
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 1 }}>
              Personnel groupings (sorted by frequency):
            </Typography>
            
            <Box component="ul" sx={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              '& li': {
                mb: 0.5
              }
            }}>
              {report.personnelAnalysis.map((personnel, index) => (
                <li key={index}>
                  <Typography variant="h6" component="span">
                    <strong>{personnel.personnel} Personnel:</strong> {personnel.totalPlays} plays ({personnel.percentOfTotal}%)
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
          
          {/* Detailed Personnel Analysis */}
          <Box sx={{ mt: 2 }}>
            {report.personnelAnalysis.map((personnel, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 1 }}>
                {personnel.personnel} PERSONNEL ({personnel.totalPlays} plays - {personnel.percentOfTotal}% of goal line plays)
              </Typography>
              
              <Typography variant="h6" component="span" sx={{ display: 'block', mb: 0.5 }}>
                <strong>Total Runs:</strong> {personnel.runs} ({personnel.runPercentage}%)
                {personnel.runs / personnel.totalPlays >= 0.8 && ' ❗ EXTREMELY VALUABLE RUN TENDENCY'}
                {personnel.runs / personnel.totalPlays >= 0.7 && personnel.runs / personnel.totalPlays < 0.8 && ' ⚠️ VALUABLE RUN TENDENCY'}
              </Typography>
              
              <Typography variant="h6" component="span" sx={{ display: 'block', mb: 1 }}>
                <strong>Total Passes:</strong> {personnel.passes} ({personnel.passPercentage}%)
                {personnel.passes / personnel.totalPlays >= 0.8 && ' ❗ EXTREMELY VALUABLE PASS TENDENCY'}
                {personnel.passes / personnel.totalPlays >= 0.7 && personnel.passes / personnel.totalPlays < 0.8 && ' ⚠️ VALUABLE PASS TENDENCY'}
              </Typography>

              {/* Formations within Personnel */}
              {personnel.formations.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 0.5 }}>
                    Formations within {personnel.personnel} Personnel:
                  </Typography>
                  
                  <Box component="ol" sx={{ 
                    listStyle: 'decimal', 
                    paddingLeft: 2, 
                    margin: 0,
                    '& li': {
                      mb: 0.3
                    }
                  }}>
                    {personnel.formations.map((formation, fIndex) => (
                      <li key={fIndex}>
                        <Typography variant="h6" component="span">
                          <strong>{formation.formation}:</strong> {formation.totalPlays} plays - 
                          Runs: {formation.runs} ({formation.runPercentage}%)
                          {formation.runs / formation.totalPlays >= 0.8 && ' ❗ EXTREMELY VALUABLE RUN TENDENCY'}
                          {formation.runs / formation.totalPlays >= 0.7 && formation.runs / formation.totalPlays < 0.8 && ' ⚠️ VALUABLE RUN TENDENCY'}
                          {' '}- Passes: {formation.passes} ({formation.passPercentage}%)
                          {formation.passes / formation.totalPlays >= 0.8 && ' ❗ EXTREMELY VALUABLE PASS TENDENCY'}
                          {formation.passes / formation.totalPlays >= 0.7 && formation.passes / formation.totalPlays < 0.8 && ' ⚠️ VALUABLE PASS TENDENCY'}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Run Concepts for this Personnel */}
              {personnel.runConcepts && Object.keys(personnel.runConcepts).length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 0.5 }}>
                    Run Concepts in {personnel.personnel} Personnel ({personnel.runs} total run plays):
                  </Typography>
                  
                  <Box component="ul" sx={{ 
                    listStyle: 'none', 
                    padding: 0, 
                    margin: 0,
                    '& li': {
                      mb: 0.2,
                      '&:before': {
                        content: '"- "',
                        fontWeight: 'bold'
                      }
                    }
                  }}>
                    {Object.entries(personnel.runConcepts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([concept, count]) => {
                        const percentage = ((count / personnel.runs) * 100).toFixed(1);
                        return (
                          <li key={concept}>
                            <Typography variant="h6" component="span">
                              <strong>{concept}:</strong> {count} plays ({percentage}%)
                            </Typography>
                          </li>
                        );
                      })}
                  </Box>
                </Box>
              )}
            </Box>
          ))}
          </Box>

          {/* Concepts Section - Text format to match other sections */}
        {Object.keys(report.runConcepts).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2 }}>
              RUN CONCEPTS
            </Typography>
            
            <Box component="ul" sx={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              '& li': {
                mb: 0.5,
                '&:before': {
                  content: '"- "',
                  fontWeight: 'bold'
                }
              }
            }}>
              {Object.entries(report.runConcepts)
                .sort((a, b) => b[1] - a[1])
                .map(([concept, count]) => {
                  const percentage = ((count / report.totalRuns) * 100).toFixed(1);
                  return (
                    <li key={concept}>
                      <Typography variant="h6" component="span">
                        <strong>{concept}:</strong> {count} plays ({percentage}%)
                      </Typography>
                    </li>
                  );
                })}
            </Box>
          </Box>
        )}

        {Object.keys(report.passConcepts).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2 }}>
              PASS CONCEPTS
            </Typography>
            
            <Box component="ul" sx={{ 
              listStyle: 'none', 
              padding: 0, 
              margin: 0,
              '& li': {
                mb: 0.5,
                '&:before': {
                  content: '"- "',
                  fontWeight: 'bold'
                }
              }
            }}>
              {Object.entries(report.passConcepts)
                .sort((a, b) => b[1] - a[1])
                .map(([concept, count]) => {
                  const percentage = ((count / report.totalPasses) * 100).toFixed(1);
                  return (
                    <li key={concept}>
                      <Typography variant="h6" component="span">
                        <strong>{concept}:</strong> {count} plays ({percentage}%)
                      </Typography>
                    </li>
                  );
                })}
            </Box>
          </Box>
        )}

        </Box> {/* End goalline-summary-section */}

        {/* Key Tendencies - Separate section for clean page breaks */}
        {(report.extremeTendencies.length > 0 || report.valuableTendencies.length > 0) && (
          <Box sx={{ mb: 2 }} data-section="goalline-tendencies-section">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2, borderBottom: '2px solid #000', py: 1 }}>
              KEY TENDENCIES
            </Typography>
            
            {report.extremeTendencies.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 2, bgcolor: '#ffebee', p: 1, border: '1px solid #d32f2f' }}>
                  🚨 EXTREME TENDENCIES (80%+), 3 or more plays.
                </Typography>
                <Box component="ul" sx={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0,
                  '& li': {
                    mb: 0.5,
                    '&:before': {
                      content: '"• "',
                      fontWeight: 'bold'
                    }
                  }
                }}>
                  {report.extremeTendencies
                    .filter(tendency => {
                      // Extract play count from tendency string (e.g., "(5/5)" or "(16/16)")
                      const match = tendency.match(/\((\d+)\/\d+\)/);
                      const playCount = match ? parseInt(match[1]) : 0;
                      return playCount >= 3;
                    })
                    .map((tendency, index) => (
                    <li key={index}>
                      <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                        {tendency}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}

            {report.valuableTendencies.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 2, bgcolor: '#fff3e0', p: 1, border: '1px solid #f57c00' }}>
                  ⚠️ VALUABLE TENDENCIES (70-79%)
                </Typography>
                <Box component="ul" sx={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: 0,
                  '& li': {
                    mb: 0.5,
                    '&:before': {
                      content: '"• "',
                      fontWeight: 'bold'
                    }
                  }
                }}>
                  {report.valuableTendencies
                    .filter(tendency => {
                      // Extract play count from tendency string (e.g., "(5/5)" or "(16/16)")
                      const match = tendency.match(/\((\d+)\/\d+\)/);
                      const playCount = match ? parseInt(match[1]) : 0;
                      return playCount >= 3;
                    })
                    .map((tendency, index) => (
                    <li key={index}>
                      <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                        {tendency}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Data Verification - Text format to match other sections */}
        <Box sx={{ mb: 2 }} data-section="goalline-verification-section">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000', mb: 2 }}>
            DATA VERIFICATION
          </Typography>
          
          <Box component="ul" sx={{ 
            listStyle: 'none', 
            padding: 0, 
            margin: 0,
            '& li': {
              mb: 0.5,
              '&:before': {
                content: '"- "',
                fontWeight: 'bold'
              }
            }
          }}>
            <li>
              <Typography variant="h6" component="span">
                <strong>Field Position Filter Applied:</strong> {report.verification.fieldPositionFilter ? '✅ YES' : '❌ NO'}
              </Typography>
            </li>
            <li>
              <Typography variant="h6" component="span">
                <strong>Total Plays Verified:</strong> {report.verification.totalPlays}
              </Typography>
            </li>
            <li>
              <Typography variant="h6" component="span">
                <strong>Personnel Sum Check:</strong> {report.verification.personnelSum}
              </Typography>
            </li>
            <li>
              <Typography variant="h6" component="span">
                <strong>Run/Pass Sum Check:</strong> {report.verification.runPassSum ? '✅ PASSED' : '❌ FAILED'}
              </Typography>
            </li>
          </Box>
        </Box>
        
        </Box> {/* End Report Content */}
      </Box>
    );
  };

  const saveReportAsPDF = async () => {
    await generateStandardizedPDF(
      REPORT_SECTIONS.GOAL_LINE,
      'goal-line-report.pdf',
      setError
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          component={Link}
          to="/reports"
          variant="outlined"
          startIcon={<ArrowLeft size={16} />}
        >
          Back to Reports
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FileText size={32} />
        Goal Line Analysis Report
      </Typography>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Upload your CSV file containing play-by-play data to generate a comprehensive goal line analysis report. 
        The report will include personnel tendencies, formation analysis, and play concepts within 5 yards of the goal line.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Upload CSV Data</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a CSV file containing your play-by-play data. The file should include columns for field position, 
            run/pass, personnel, formations, and play concepts.
          </Typography>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="csv-file-upload"
            disabled={loading}
          />
          <label htmlFor="csv-file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<Upload size={16} />}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              Choose CSV File
            </Button>
          </label>

          {file && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected file:</strong> {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={processData}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <FileText />}
          size="large"
        >
          {loading ? 'Generating Report...' : 'Generate Goal Line Report'}
        </Button>
        
        <Button
          variant="outlined"
          onClick={clearAll}
          disabled={loading}
        >
          Clear All
        </Button>
      </Box>

      {loading && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="h6">Processing your data...</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              The server is analyzing your CSV data and generating the goal line report. This may take a few moments.
            </Typography>
          </CardContent>
        </Card>
      )}

      {report && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>Generated Report</Typography>
          
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={saveReportAsPDF}
            >
              Save as PDF
            </Button>
          </Box>

          <Box id="styled-report-content">
            {renderReport()}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GoalLineReportGenerator;